/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { OrganizationType } from "@slr/user-service-external-sdk";
import { OnboardingType } from "configs";
import { createContext, useContext } from "react";

export type SolutionBarQuery = {
  solutionURL?: string;
  solutionName?: string;
  solutionColor?: string;
  showEncryption?: boolean;
  showOrgEdit?: boolean;
};

export type ChooseOrganizationQuery = {
  redirectURL?: string;
  solutionName?: string;
  suggestedOrganizationName?: string;
  organizationType?: OrganizationType[];
};

export type InvitationQuery = {
  token?: string;
  organizationId?: string;
  acceptImmediately?: boolean;
};

export type OnboardingQuery = {
  onboardingType?: OnboardingType;
  redirectURL?: string;
};

export type QueryContextParamsValues = {
  showSolutionBar?: boolean;
  showLayout?: boolean;
  solutionBarQuery?: SolutionBarQuery;
  chooseOrganizationQuery?: ChooseOrganizationQuery;
  invitationQuery?: InvitationQuery;
  onboardingQuery?: OnboardingQuery;
  removeQueryParamsFromURL: (
    param: string,
    resetValue: boolean | string
  ) => void;
};

const defaultQueryParamContextValues: QueryContextParamsValues = {
  showSolutionBar: false,
  showLayout: true,
  solutionBarQuery: {},
  chooseOrganizationQuery: {},
  invitationQuery: {},
  onboardingQuery: {},
  removeQueryParamsFromURL: () => {},
};

export const QueryParamContext = createContext<QueryContextParamsValues>(
  defaultQueryParamContextValues
);

export const useQueryParam = () => useContext(QueryParamContext);
